import React, { useEffect, useState } from "react";
import {
  Plug,
  CheckCircle,
  XCircle,
  MessageCircle,
  Trello,
  CreditCard,
} from "lucide-react";
import { hubSpotService } from "../../services/HubSpotService";
import { stripeIntegrationService } from "../../services/StripeIntegrationService";
import { useToastContext } from "../../context/ToastContext";
import ConfirmDisconnectModal from "../hubspot/ConfirmDisconnectModal";
import { useLoader } from "../../context/LoaderContext";
import { formatDate } from "../../utils/DateUtils";

interface ConnectionStatus {
  connected: boolean;
  loading: boolean;
  createdAt?: string;
  comingSoon?: boolean;
}

const ThirdPartyConnections: React.FC = () => {
  const [connections, setConnections] = useState<{
    hubspot: ConnectionStatus;
    stripe: ConnectionStatus;
    slack: ConnectionStatus;
    asana: ConnectionStatus;
  }>({
    hubspot: { connected: false, loading: true },
    stripe: { connected: false, loading: true },
    slack: { connected: false, loading: false, comingSoon: true },
    asana: { connected: false, loading: false, comingSoon: true },
  });

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(
    null
  );

  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    const checkConnectionStatuses = async () => {
      try {
        const [hubspotResult, stripeResult] = await Promise.allSettled([
          hubSpotService.getHubSpotStatus(),
          stripeIntegrationService.getStripeStatus(),
        ]);

        const hubspotStatus =
          hubspotResult.status === "fulfilled"
            ? hubspotResult.value
            : { data: { connected: false }, message: "Not connected" };
        const stripeStatus =
          stripeResult.status === "fulfilled"
            ? stripeResult.value
            : { data: { connected: false }, message: "Not connected" };

        setConnections((prev) => ({
          ...prev,
          hubspot: {
            connected: hubspotStatus.data?.connected ?? false,
            loading: false,
            createdAt: hubspotStatus.data?.createdAt,
          },
          stripe: {
            connected: stripeStatus.data?.connected ?? false,
            loading: false,
            createdAt: stripeStatus.data?.createdAt,
          },
        }));
      } catch (error: any) {
        console.error("Failed to fetch connection statuses:", error);
        setConnections((prev) => ({
          ...prev,
          hubspot: { connected: false, loading: false },
          stripe: { connected: false, loading: false },
        }));
      }
    };

    checkConnectionStatuses();
  }, [showToast]);

  const getIntegrationIcon = (integration: string) => {
    switch (integration) {
      case "hubspot":
        return (
          <div className="w-12 h-12 rounded-xl bg-[#FF7A59] flex items-center justify-center shadow-sm">
            <Plug className="w-6 h-6 text-white" />
          </div>
        );
      case "stripe":
        return (
          <div className="w-12 h-12 rounded-xl bg-[#635BFF] flex items-center justify-center shadow-sm">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-12 h-12 rounded-xl bg-background-panel dark:bg-secondary/10 flex items-center justify-center">
            <Plug className="w-6 h-6 text-secondary" />
          </div>
        );
    }
  };

  const handleConnect = async (integration: string) => {
    switch (integration) {
      case "hubspot":
        try {
          const { data } = await hubSpotService.connectHubSpot();
          if (data.authorizationUrl) {
            window.location.href = data.authorizationUrl;
          }
        } catch (error) {
          showToast("error", "Failed to connect to HubSpot.");
        }
        break;
      case "stripe":
        try {
          const { data } = await stripeIntegrationService.connectStripe();
          if (data.authorizationUrl) {
            window.location.href = data.authorizationUrl;
          }
        } catch (error) {
          showToast("error", "Failed to connect to Stripe.");
        }
        break;
      case "slack":
      case "asana":
        // Coming soon - show a fun message
        showToast("info", "This integration is coming soon! 🚀");
        break;
    }
  };

  const handleDisconnect = (integration: string) => {
    setSelectedIntegration(integration);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    setShowDisconnectModal(false);
    showLoader();

    try {
      if (selectedIntegration === "hubspot") {
        await hubSpotService.disconnectHubSpot();
        setConnections((prev) => ({
          ...prev,
          hubspot: { connected: false, loading: false },
        }));
        showToast("success", "HubSpot disconnected successfully.");
      } else if (selectedIntegration === "stripe") {
        await stripeIntegrationService.disconnectStripe();
        setConnections((prev) => ({
          ...prev,
          stripe: { connected: false, loading: false },
        }));
        showToast("success", "Stripe disconnected successfully.");
      }
      
      // Dispatch custom event to notify other components (like Home page)
      window.dispatchEvent(new CustomEvent("connectionStatusChanged"));
    } catch (error) {
      showToast("error", `Failed to disconnect from ${selectedIntegration}.`);
    } finally {
      hideLoader();
      setSelectedIntegration(null);
    }
  };


  // Filter out coming soon connectors for the main display
  const activeConnectors = Object.entries(connections).filter(
    ([_, value]) => !value.comingSoon
  );

  return (
    <div className="w-full flex flex-col">
      <div className="bg-background-default dark:bg-background-dark">
        <div className="px-6 py-6">
          <h1 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-1">
            Connectors
          </h1>
          <p className="text-sm text-black/60 dark:text-text-secondaryDark">
            Connect your tools to sync data and automate workflows
          </p>
        </div>
      </div>

      <div className="flex-1 px-6 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          {activeConnectors.map(([key, value]) => (
            <div
              key={key}
              className="group relative bg-background-paper dark:bg-background-paperDark rounded-lg border border-black/10 dark:border-divider-dark p-6 transition-all duration-200 hover:shadow-md"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getIntegrationIcon(key)}
                    <div>
                      <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark">
                        {key === "hubspot" ? "HubSpot" : "Stripe"}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        {value.loading ? (
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 bg-black/40 dark:bg-text-secondaryDark rounded-full animate-pulse"></div>
                            <span className="text-xs text-black/60 dark:text-text-secondaryDark">
                              Checking...
                            </span>
                          </div>
                        ) : value.connected ? (
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs font-medium text-green-600 dark:text-green-400">
                              Connected
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 bg-black/30 dark:bg-text-secondaryDark rounded-full"></div>
                            <span className="text-xs text-black/60 dark:text-text-secondaryDark">
                              Not connected
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 mb-4">
                  <p className="text-sm text-black/70 dark:text-text-secondaryDark leading-relaxed">
                    {key === "hubspot"
                      ? "Sync your CRM data, deals, contacts, and companies for real-time insights."
                      : "Connect your billing platform to track subscriptions, payments, and revenue."}
                  </p>
                  {value.connected && value.createdAt && (
                    <p className="text-xs text-black/50 dark:text-text-secondaryDark/70 mt-3">
                      Connected {formatDate(value.createdAt)}
                    </p>
                  )}
                </div>

                <div className="pt-4 border-t border-black/5 dark:border-divider-dark">
                  {value.connected ? (
                    <button
                      onClick={() => handleDisconnect(key)}
                      className="w-full px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 bg-background-paper dark:bg-background-paperDark border border-red-200 dark:border-red-800/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 hover:border-red-300 dark:hover:border-red-700/40 transition-all duration-200"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => handleConnect(key)}
                      className="w-full px-4 py-2.5 bg-background-paper dark:bg-background-paperDark border border-black dark:border-divider-dark text-black dark:text-white text-sm font-medium rounded-lg hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-black/20 dark:hover:border-divider-dark transition-all duration-200"
                    >
                      Connect {key === "hubspot" ? "HubSpot" : "Stripe"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <ConfirmDisconnectModal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          onConfirm={confirmDisconnect}
          integration={selectedIntegration || ""}
        />
      </div>
    </div>
  );
};

export default ThirdPartyConnections;
